<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Table from 'dashboard/components-next/table/Table.vue';
import TableEmpty from 'dashboard/components-next/table/TableEmpty.vue';
import { relativeTime } from 'dashboard/components-next/Contacts/contactTime';

// Table view of the companies list, same grammar as the contacts table:
// Empresa (avatar + name + domain) · Contactos · Descripción · Actualizada.
// Clicking a row opens the edit dialog; the contacts cell jumps to the
// contacts list searched by the company name.
defineProps({
  companies: { type: Array, required: true },
});

const emit = defineEmits(['edit', 'viewContacts']);

const { t, locale } = useI18n();

const toUnix = value => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : Math.round(ms / 1000);
};
const updatedLabel = company =>
  relativeTime(toUnix(company.updatedAt), locale.value);

const columns = computed(() => [
  { key: 'company', label: t('COMPANIES.TABLE.COMPANY'), width: 'w-[30%]' },
  { key: 'contacts', label: t('COMPANIES.TABLE.CONTACTS'), width: 'w-[16%]' },
  { key: 'description', label: t('COMPANIES.TABLE.DESCRIPTION') },
  {
    key: 'updated',
    label: t('COMPANIES.TABLE.UPDATED'),
    width: 'w-[14%]',
    cellClass: 'text-xs text-n-slate-11 whitespace-nowrap',
  },
]);
</script>

<template>
  <Table
    :columns="columns"
    :rows="companies"
    clickable
    @row-click="company => emit('edit', company)"
  >
    <template #cell-company="{ row }">
      <div class="flex items-center gap-2.5 min-w-0">
        <Avatar
          :name="row.name || '?'"
          :src="row.avatarUrl || ''"
          :size="28"
          rounded-full
        />
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ row.name || t('COMPANIES.UNNAMED') }}
          </span>
          <span
            v-if="row.domain"
            class="inline-flex items-center gap-1 text-xs text-n-slate-10 truncate"
          >
            <Icon icon="i-lucide-globe" size="size-3 flex-shrink-0" />
            <span class="truncate">{{ row.domain }}</span>
          </span>
        </div>
      </div>
    </template>

    <template #cell-contacts="{ row }">
      <button
        v-if="row.contactsCount"
        type="button"
        class="inline-flex items-center gap-1.5 h-6 px-2 rounded-md text-xs font-medium bg-n-alpha-2 text-n-slate-11 hover:bg-n-brand/10 hover:text-n-blue-text transition-colors"
        :title="t('COMPANIES.TABLE.VIEW_CONTACTS')"
        @click.stop="emit('viewContacts', row)"
      >
        <Icon icon="i-lucide-users" size="size-3" />
        {{
          t(
            'COMPANIES.CONTACTS_COUNT',
            { n: row.contactsCount },
            row.contactsCount
          )
        }}
      </button>
      <TableEmpty v-else :label="t('COMPANIES.TABLE.NO_CONTACTS')" />
    </template>

    <template #cell-description="{ row }">
      <span
        v-if="row.description"
        class="block text-sm text-n-slate-11 truncate"
        :title="row.description"
      >
        {{ row.description }}
      </span>
      <TableEmpty v-else />
    </template>

    <template #cell-updated="{ row }">
      <div class="flex items-center gap-2">
        <span class="truncate">{{ updatedLabel(row) || '—' }}</span>
        <Icon
          icon="i-lucide-pencil"
          size="size-3.5 ml-auto text-n-slate-9 opacity-0 group-hover/row:opacity-100 transition-opacity flex-shrink-0"
        />
      </div>
    </template>
  </Table>
</template>
